import * as React from 'react';

import { DateTime } from 'luxon';
import { UniqueIdentifier, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  List,
} from '@mui/material';

import { grey } from '@mui/material/colors';

import SortableEventReportWrapper from './SortableEventReportWrapper';
import SortableEventReport from './SortableEventReport';
import PropertyStack from '../PropertyStack';

import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';
import { INDIVIDUAL_REPORTS_COLLECTION_ID } from '../../core/context/UnattachedEventReportsContext';
import { useUnattachedEventReportsSelectionData } from '../../core/context/UnattachedEventReportsSelectionContext';

import { EventReport } from '../../core/api/clients';
import CreateIncidentButton from './CreateIncidentButton';

interface UnattachedEventReportsGroupProps {
  containerId: UniqueIdentifier,
  eventReports: EventReport[],
  title?: string,
}

function UnattachedEventReportsGroup({ containerId, eventReports, title }: UnattachedEventReportsGroupProps) {
  const { isAdministrator } = useAuthorizedFormData();
  const { checkedContainer, handleCheckedContainerChange } = useUnattachedEventReportsSelectionData();
  const { setNodeRef } = useDroppable({
    id: containerId,
  });

  const handleCheckboxChange = () => {
    handleCheckedContainerChange(checkedContainer !== containerId ? containerId : null);
  };
  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (!eventReports) return <React.Fragment />;
  return (
    <Card sx={{ margin: 2 }} raised={checkedContainer === containerId}>
      <CardHeader
        action={
          containerId !== INDIVIDUAL_REPORTS_COLLECTION_ID
            ? <Checkbox checked={checkedContainer === containerId} onChange={handleCheckboxChange} />
            : null
        }
        title={title || (
          <PropertyStack
            direction="row"
            spacing={2}
            divider={<Divider orientation="vertical" flexItem />}
            properties={{
              'Flight Number': eventReports[0].flightNumber,
              'Event Date': DateTime.fromISO(`${eventReports[0].eventDate}`).toLocaleString(DateTime.DATE_SHORT),
              'Departure Airport': eventReports[0].departureAirport,
              'Scheduled Arrival Airport': eventReports[0].scheduledArrivalAirport,
            }}
          />
        )}
        sx={{
          backgroundColor: grey['200'],
        }}
      />
      <CardContent>
        <SortableContext
          id={containerId as string}
          items={eventReports && eventReports.map((e) => e.id ?? 0)}
          strategy={verticalListSortingStrategy}
        >
          <List ref={setNodeRef}>
            {eventReports && eventReports.map((eventReport, index) => (
              <React.Fragment key={eventReport.id}>
                <SortableEventReportWrapper
                  key={eventReport.id}
                  id={eventReport.id ?? 0}
                  renderEventReport={(handleAttributes, handleListeners) => (
                    <SortableEventReport
                      eventReport={eventReport}
                      parentContainerId={containerId}
                      draggableAttributes={handleAttributes}
                      draggableListeners={handleListeners}
                    />
                  )}
                />
                {index < eventReports.length - 1 && containerId === INDIVIDUAL_REPORTS_COLLECTION_ID && (
                  <Divider component="li" sx={{ marginTop: 2, marginBottom: 2 }} />
                )}
              </React.Fragment>
            ))}
          </List>
        </SortableContext>
      </CardContent>
      {containerId !== INDIVIDUAL_REPORTS_COLLECTION_ID && isAdministrator && (
        <CardActions>
          <CreateIncidentButton eventReports={eventReports} />
        </CardActions>
      )}
    </Card>
  );
}

export default UnattachedEventReportsGroup;
